/** 时间线轮次 `at` 落库格式（含日期，便于跨年展示） */
const TIMELINE_AT_STAMP_RE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})/
const TIME_ONLY_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/
const YEAR_SEG_RE = /^(19|20)\d{2}$/
const NUM_FOLDER_SEG_RE = /^\d{1,2}$/

export type TimelineRailDateLabels = {
  /** 嵌入时间线轨顶部的年份（如 2026） */
  yearLabel: string | null
  /** 月·日（如 5·26）；无完整日期时为 null */
  monthDayLabel: string | null
  /** 时刻 HH:mm */
  timeLabel: string
  /** @deprecated 兼容旧调用，等同 {@link timeLabel} 或历史原文 */
  primaryLabel: string
  /** 悬停 title */
  title: string
}

/** 轨侧月日：与 HH:mm 时刻区分，用中文日历格式 */
function monthDayFromStamp(month: string, day: string): string {
  const m = Number.parseInt(month, 10)
  const d = Number.parseInt(day, 10)
  if (!Number.isFinite(m) || !Number.isFinite(d)) return `${month}月${day}日`
  return `${m}月${d}日`
}

function timeFromParts(hour: string, minute: string): string {
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
}

/** 新建/更新轮次时写入的 `at` */
export function stampTimelineRoundAt(date = new Date()): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d} ${hh}:${mm}`
}

/** 后端 ISO 时间 → 时间线 `at` 戳 */
export function timelineAtFromBackendIso(iso: string | null | undefined): string | null {
  const raw = iso?.trim()
  if (!raw) return null
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return null
  return stampTimelineRoundAt(d)
}

/**
 * 时间线轨侧展示：年份置顶，下方为原有主标签（多为 HH:mm）。
 * 兼容历史仅存时刻、locale 月日、以及新 yyyy-MM-dd HH:mm 戳。
 */
export function formatTimelineRailDateLabels(at: string): TimelineRailDateLabels {
  const raw = at.trim()
  if (!raw) {
    return { yearLabel: null, monthDayLabel: null, timeLabel: '', primaryLabel: '', title: '' }
  }

  const stamp = raw.match(TIMELINE_AT_STAMP_RE)
  if (stamp) {
    const yearLabel = stamp[1]
    const monthDayLabel = monthDayFromStamp(stamp[2], stamp[3])
    const timeLabel = timeFromParts(stamp[4], stamp[5])
    return { yearLabel, monthDayLabel, timeLabel, primaryLabel: timeLabel, title: raw }
  }

  const parsed = Date.parse(raw)
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed)
    const y = String(d.getFullYear())
    const m = String(d.getMonth() + 1)
    const day = String(d.getDate())
    const timeLabel = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
    return {
      yearLabel: y,
      monthDayLabel: monthDayFromStamp(m, day),
      timeLabel,
      primaryLabel: timeLabel,
      title: raw,
    }
  }

  if (TIME_ONLY_RE.test(raw)) {
    const now = new Date()
    return {
      yearLabel: String(now.getFullYear()),
      monthDayLabel: monthDayFromStamp(String(now.getMonth() + 1), String(now.getDate())),
      timeLabel: raw,
      primaryLabel: raw,
      title: raw,
    }
  }

  if (/月/.test(raw) && !/年/.test(raw)) {
    const now = new Date()
    return {
      yearLabel: String(now.getFullYear()),
      monthDayLabel: null,
      timeLabel: raw,
      primaryLabel: raw,
      title: raw,
    }
  }

  const yearInText = raw.match(/(20\d{2})\s*年?/)
  if (yearInText) {
    const yearLabel = yearInText[1]
    const rest = raw.replace(yearInText[0], '').trim()
    const timeMatch = rest.match(TIME_ONLY_RE)
    const timeLabel = timeMatch ? timeMatch[0] : rest || raw
    return {
      yearLabel,
      monthDayLabel: null,
      timeLabel,
      primaryLabel: timeLabel,
      title: raw,
    }
  }

  return {
    yearLabel: null,
    monthDayLabel: null,
    timeLabel: raw,
    primaryLabel: raw,
    title: raw,
  }
}

/** 漫画书架：更新日期（含年） */
export function formatShelfDate(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

/** 图库详情/灯箱：统一为含四位数年份的可读时间 */
export function formatGalleryDateTime(raw: string | null | undefined): string {
  const t = raw?.trim() ?? ''
  if (!t) return '-'

  const stamp = t.match(TIMELINE_AT_STAMP_RE)
  if (stamp) {
    return `${stamp[1]}-${stamp[2]}-${stamp[3]} ${stamp[4].padStart(2, '0')}:${stamp[5]}`
  }

  const parsed = Date.parse(t)
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    const ss = String(d.getSeconds()).padStart(2, '0')
    if (TIME_ONLY_RE.test(t) || t.includes('T')) {
      return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
    }
    return `${y}-${m}-${day} ${hh}:${mm}`
  }

  const rail = formatTimelineRailDateLabels(t)
  if (rail.yearLabel && TIME_ONLY_RE.test(rail.primaryLabel)) {
    const now = new Date()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${rail.yearLabel}-${m}-${day} ${rail.primaryLabel}`
  }

  if (/年/.test(t) || YEAR_SEG_RE.test(t)) return t

  if (/月/.test(t)) {
    return `${new Date().getFullYear()}年${t}`
  }

  return t
}

/**
 * 图库侧栏/面包屑：日期类文件夹段更易读（2026 → 2026年，其下 05 → 5月）。
 */
export function formatLibraryFolderSegment(segment: string, parentUiPath: string): string {
  const seg = segment.trim()
  if (!seg) return seg
  if (YEAR_SEG_RE.test(seg)) return `${seg}年`

  const parts = parentUiPath.split('/').filter(Boolean)
  if (parts.length >= 1 && YEAR_SEG_RE.test(parts.at(-1)!) && NUM_FOLDER_SEG_RE.test(seg)) {
    const n = Number.parseInt(seg, 10)
    if (n >= 1 && n <= 12) return `${n}月`
  }
  if (
    parts.length >= 2 &&
    YEAR_SEG_RE.test(parts[0]!) &&
    NUM_FOLDER_SEG_RE.test(parts[1]!) &&
    NUM_FOLDER_SEG_RE.test(seg)
  ) {
    const n = Number.parseInt(seg, 10)
    if (n >= 1 && n <= 31) return `${n}日`
  }
  return seg
}
